import {useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {
  countCityWindows,
  sampleCityStreet,
  type CityBuilding,
} from './cityStreet';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {OllieHead} from './feltFriends';
import {Bounce, ToyShell, type MapToyProps} from './mapToys';

/**
 * Map-sized "goodies" in the felt style of the other map toys: a plush
 * course textbook, a record player and a T-shirt on a rack (pictures the
 * roadmap wraps in a link button), and a city street at night whose windows light up by
 * chance, after the "City at night" painting from the Art Model
 * assignment.
 */

/** Props for the goodie pictures. */
export interface GoodiePictureProps {
  /** Rendered width in px. */
  size: number;
}

const CARDINAL_DARK = '#6A0F0F';
const PAGE_LINE = '#D9C3A0';

/**
 * A plush textbook with a bell curve stitched on its cover and a ribbon
 * bookmark. Decorative: the caller supplies the button and its name.
 *
 * @param props - See {@link GoodiePictureProps}.
 * @return The picture.
 * @throws Never.
 */
export function GoodieTextbook({size}: GoodiePictureProps) {
  const ids = useFeltIds();
  return (
    <svg
      viewBox="0 0 100 110"
      width={size}
      height={size * 1.1}
      aria-hidden
      focusable="false"
    >
      <defs>
        <FeltDefs ids={ids} />
      </defs>
      <ellipse cx={52} cy={104} rx={36} ry={4} fill="rgba(0,0,0,0.08)" />
      <rect
        x={26}
        y={14}
        width={62}
        height={84}
        rx={9}
        fill={FELT.creamLight}
        stroke={FELT.creamDark}
        strokeWidth={3}
      />
      {[30, 42, 54, 66, 78].map(y => (
        <path
          key={y}
          d={`M80 ${y} H86`}
          stroke={PAGE_LINE}
          strokeWidth={1.6}
          strokeLinecap="round"
        />
      ))}
      <rect
        x={12}
        y={8}
        width={66}
        height={86}
        rx={11}
        fill={FELT.cardinal}
        filter={`url(#${ids.fuzz})`}
      />
      <rect x={12} y={8} width={13} height={86} rx={6} fill={CARDINAL_DARK} />
      <rect
        x={31}
        y={16}
        width={40}
        height={70}
        rx={7}
        fill="none"
        stroke={FELT.cream}
        strokeWidth={1.8}
        strokeDasharray="4 3.5"
        opacity={0.7}
      />
      <path
        d="M33 58 C39 58 41 52 44 44 C47 35 49 32 51 32 C53 32 55 35 58 44 C61 52 63 58 69 58 Z"
        fill={FELT.gold}
        opacity={0.35}
      />
      <path
        d="M33 58 C39 58 41 52 44 44 C47 35 49 32 51 32 C53 32 55 35 58 44 C61 52 63 58 69 58"
        fill="none"
        stroke={FELT.gold}
        strokeWidth={3.4}
        strokeLinecap="round"
      />
      <path
        d="M33 58 H69"
        stroke={FELT.cream}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <circle cx={44} cy={69} r={2.6} fill={FELT.ink} />
      <circle cx={58} cy={69} r={2.6} fill={FELT.ink} />
      <circle cx={39} cy={74} r={3} fill={FELT.pink} opacity={0.75} />
      <circle cx={63} cy={74} r={3} fill={FELT.pink} opacity={0.75} />
      <path
        d="M47.5 73 Q51 76.5 54.5 73"
        fill="none"
        stroke={FELT.ink}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
      <path d="M60 92 V104 L64 100 L68 104 V92 Z" fill={FELT.gold} />
    </svg>
  );
}

/**
 * One eighth note, its head centred on the origin.
 *
 * @param props.color - The note's fill.
 * @return The note.
 * @throws Never.
 */
function EighthNote({color}: {color: string}) {
  return (
    <g>
      <ellipse
        cx={0}
        cy={0}
        rx={4.4}
        ry={3.3}
        fill={color}
        transform="rotate(-20)"
      />
      <path
        d="M3.8 -1 V-16 Q10 -12 10.5 -5"
        fill="none"
        stroke={color}
        strokeWidth={2.2}
        strokeLinecap="round"
      />
    </g>
  );
}

const CASE = '#C98B5B';
const CASE_DARK = '#9E6A42';
const CASE_LIGHT = '#DDA676';
const VINYL = '#2A1E1C';
const VINYL_GROOVE = '#4A3A36';
const SILVER = '#C9CED6';

/**
 * A plush record player with a spinning-ready record and two notes
 * floating up. Decorative: the caller supplies the button and its name.
 *
 * @param props - See {@link GoodiePictureProps}.
 * @return The picture.
 * @throws Never.
 */
export function GoodieRecordPlayer({size}: GoodiePictureProps) {
  const ids = useFeltIds();
  return (
    <svg
      viewBox="0 0 120 100"
      width={size}
      height={(size * 100) / 120}
      aria-hidden
      focusable="false"
    >
      <defs>
        <FeltDefs ids={ids} />
      </defs>
      <ellipse cx={60} cy={95} rx={48} ry={4} fill="rgba(0,0,0,0.08)" />
      <rect
        x={10}
        y={44}
        width={100}
        height={47}
        rx={12}
        fill={CASE}
        stroke={CASE_DARK}
        strokeWidth={3}
        filter={`url(#${ids.fuzz})`}
      />
      <rect x={14} y={39} width={92} height={13} rx={6} fill={CASE_LIGHT} />
      <rect x={18} y={64} width={84} height={20} rx={8} fill={CASE_DARK} />
      <circle cx={33} cy={74} r={7.5} fill={FELT.cream} />
      <circle
        cx={33}
        cy={74}
        r={4.6}
        fill="none"
        stroke={CASE_DARK}
        strokeWidth={1.2}
        strokeDasharray="2 2"
      />
      <circle cx={80} cy={74} r={4.6} fill={FELT.gold} />
      <circle cx={93} cy={74} r={4.6} fill={FELT.gold} />
      <ellipse cx={54} cy={44} rx={35} ry={11.5} fill={VINYL} />
      <ellipse
        cx={54}
        cy={44}
        rx={27}
        ry={8.6}
        fill="none"
        stroke={VINYL_GROOVE}
        strokeWidth={1.2}
      />
      <ellipse
        cx={54}
        cy={44}
        rx={19}
        ry={6}
        fill="none"
        stroke={VINYL_GROOVE}
        strokeWidth={1.2}
      />
      <ellipse cx={54} cy={44} rx={9} ry={3.2} fill={FELT.cardinal} />
      <ellipse cx={54} cy={44} rx={1.6} ry={0.8} fill={FELT.cream} />
      <path
        d="M30 39 Q40 34.5 52 34"
        fill="none"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth={2}
        strokeLinecap="round"
      />
      <path
        d="M98 33 L88 27 L74 40"
        fill="none"
        stroke={SILVER}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x={69}
        y={38}
        width={8}
        height={5}
        rx={1.5}
        fill={SILVER}
        transform="rotate(-30 73 40.5)"
      />
      <circle cx={98} cy={33} r={5} fill={SILVER} />
      <circle cx={98} cy={33} r={2} fill={CASE_DARK} />
      <g transform="translate(30 22)">
        <EighthNote color={FELT.cardinal} />
      </g>
      <g transform="translate(50 14) scale(0.8)">
        <EighthNote color={FELT.gold} />
      </g>
    </svg>
  );
}

const RACK = '#9E6A42';
const TEE_STITCH = '#D6C7AE';

/**
 * A T-shirt on a hanger on a little clothes rack, with a design still being
 * sketched on its chest, a pencil, a gold rosette, and Ollie peeking over
 * its shoulder as on the contest page. Decorative: the caller supplies the
 * button and its name.
 *
 * @param props - See {@link GoodiePictureProps}.
 * @return The picture.
 * @throws Never.
 */
export function GoodieTshirt({size}: GoodiePictureProps) {
  const ids = useFeltIds();
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
    >
      <defs>
        <FeltDefs ids={ids} />
      </defs>
      <ellipse cx={50} cy={96} rx={44} ry={3.5} fill="rgba(0,0,0,0.08)" />
      {[10, 90].map(x => (
        <g key={x}>
          <path
            d={`M ${x} 14 V 94`}
            stroke={RACK}
            strokeWidth={3.4}
            strokeLinecap="round"
          />
          <path
            d={`M ${x - 7} 94 H ${x + 7}`}
            stroke={RACK}
            strokeWidth={3.4}
            strokeLinecap="round"
          />
        </g>
      ))}
      <path
        d="M 8 14 H 92"
        stroke={RACK}
        strokeWidth={3.6}
        strokeLinecap="round"
      />
      <g transform="translate(66 32) scale(0.85)">
        <OllieHead mood="happy" />
      </g>
      <path
        d="M 50 14 Q 54 14 54 18 Q 54 21 50 22 V 25"
        fill="none"
        stroke="#A8A29E"
        strokeWidth={1.6}
        strokeLinecap="round"
      />
      <path
        d="M 50 25 L 26 33 H 74 Z"
        fill="none"
        stroke={CASE_DARK}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      <path
        d="M 40 31 L 45 29 Q 50 35 55 29 L 60 31 L 77 40 L 71 52 L 64 49 V 90 H 36 V 49 L 29 52 L 23 40 Z"
        fill={FELT.creamLight}
        stroke={TEE_STITCH}
        strokeWidth={2}
        strokeLinejoin="round"
        filter={`url(#${ids.fuzz})`}
      />
      <path
        d="M 45 29 Q 50 35 55 29"
        fill="none"
        stroke={FELT.cardinal}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <path
        d="M 50 55 L 53 62 L 60.5 62.5 L 54.8 67.3 L 56.8 74.6 L 50 70.4 L 43.2 74.6 L 45.2 67.3 L 39.5 62.5 L 47 62 Z"
        fill="none"
        stroke={FELT.ink}
        strokeWidth={1.2}
        strokeDasharray="2.2 1.8"
        strokeLinejoin="round"
        opacity={0.7}
      />
      <g transform="translate(64 74) rotate(-35)">
        <rect x={-2} y={-16} width={4} height={16} rx={1} fill={FELT.gold} />
        <path d="M -2 0 L 0 4.5 L 2 0 Z" fill={FELT.cream} />
        <path d="M -0.7 3 L 0 4.5 L 0.7 3 Z" fill={FELT.ink} />
        <rect x={-2} y={-18.5} width={4} height={2.5} rx={1} fill={FELT.pink} />
      </g>
      <g transform="translate(39 44)">
        <path d="M -3 2 L -5 11 L -1 8.5 Z" fill={FELT.cardinal} />
        <path d="M 3 2 L 5 11 L 1 8.5 Z" fill={FELT.cardinal} />
        <circle r={5.4} fill={FELT.gold} filter={`url(#${ids.fuzz})`} />
        <circle r={2.6} fill="#F3C969" />
      </g>
    </svg>
  );
}

/** How many buildings the toy's street has. */
const CITY_BUILDINGS = 7;
/** How many floors a building on the toy's street could have. */
const CITY_MAX_FLOORS = 7;

/** The painting's night colours. */
const NIGHT = {
  sky: '#060918',
  horizon: '#0D1430',
  wall: '#0B0E18',
  wallEdge: '#1B2238',
  lit: '#FFD27A',
  unlit: '#5F6D8A',
  moon: '#FFF1CF',
  ground: '#1A1F33',
} as const;

/** The street the palette thumbnail shows, so it never changes. */
const PREVIEW_STREET: readonly CityBuilding[] = [
  {isOffice: false, windows: [true, true, false, true]},
  {isOffice: true, windows: [false, false, false, false, false, true]},
  {isOffice: false, windows: [true, false, true]},
  {isOffice: false, windows: [false, true, true, true, false, true, true]},
  {isOffice: false, windows: [true, true, false, true, true]},
  {isOffice: true, windows: [false, false, false, false]},
  {isOffice: false, windows: [true, false, true, true, true]},
];

const STARS: ReadonlyArray<readonly [number, number, number]> = [
  [18, 18, 1.1],
  [32, 30, 0.8],
  [46, 14, 1],
  [66, 22, 0.8],
  [74, 12, 1.1],
  [108, 44, 0.9],
  [16, 46, 0.8],
];

const switchOn = keyframes`
  0% { opacity: 0; }
  60% { opacity: 1; }
  75% { opacity: 0.55; }
  100% { opacity: 1; }
`;

/** A lit window that flickers on after a short delay when it mounts. */
const LitWindow = styled.g<{$delaySec: number}>`
  animation: ${switchOn} 0.5s ease-out ${({$delaySec}) => $delaySec}s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FLOOR_HEIGHT = 10;
const BUILDING_WIDTH = 13;
const STREET_LEFT = 14.5;
const GROUND_Y = 98;
const WINDOW_SIZE = 7;

/**
 * City lights: a little street at night whose windows are each lit by
 * chance, drawn with the "City at night" painting's story. Click for a
 * fresh street.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyCityLights({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const clipId = `${ids.soft}-city`;
  const [draws, setDraws] = useState(0);
  const [street, setStreet] = useState<readonly CityBuilding[]>(() =>
    isPreview
      ? PREVIEW_STREET
      : sampleCityStreet({
          buildings: CITY_BUILDINGS,
          maxFloors: CITY_MAX_FLOORS,
        }),
  );
  const {lit, total} = countCityWindows(street);

  return (
    <ToyShell
      isPreview={isPreview}
      label="City lights"
      caption={`${lit} of ${total} windows lit`}
      onPoke={() => {
        setDraws(draws + 1);
        setStreet(
          sampleCityStreet({
            buildings: CITY_BUILDINGS,
            maxFloors: CITY_MAX_FLOORS,
          }),
        );
      }}
    >
      <Bounce key={draws} $animation="pop">
        <svg
          viewBox="0 0 120 120"
          width={size}
          height={size}
          aria-hidden
          focusable="false"
        >
          <defs>
            <FeltDefs ids={ids} />
            <clipPath id={clipId}>
              <rect x={6} y={6} width={108} height={108} rx={22} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <rect x={0} y={0} width={120} height={120} fill={NIGHT.sky} />
            <rect
              x={0}
              y={66}
              width={120}
              height={32}
              fill={NIGHT.horizon}
              opacity={0.7}
            />
            {STARS.map(([cx, cy, r]) => (
              <circle
                key={`${cx}-${cy}`}
                cx={cx}
                cy={cy}
                r={r}
                fill={NIGHT.moon}
                opacity={0.8}
              />
            ))}
            <circle cx={94} cy={24} r={15} fill={NIGHT.moon} opacity={0.12} />
            <circle cx={94} cy={24} r={8.5} fill={NIGHT.moon} />
            {street.map((building, index) => {
              const x = STREET_LEFT + index * BUILDING_WIDTH;
              const height = building.windows.length * FLOOR_HEIGHT;
              return (
                // Buildings never reorder within one street.
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <g key={index}>
                  {height > 0 && (
                    <rect
                      x={x}
                      y={GROUND_Y - height}
                      width={BUILDING_WIDTH}
                      height={height}
                      fill={NIGHT.wall}
                      stroke={NIGHT.wallEdge}
                      strokeWidth={0.6}
                    />
                  )}
                  {building.windows.map((isLit, floor) => {
                    const wx = x + (BUILDING_WIDTH - WINDOW_SIZE) / 2;
                    const wy =
                      GROUND_Y -
                      (floor + 1) * FLOOR_HEIGHT +
                      (FLOOR_HEIGHT - WINDOW_SIZE) / 2;
                    if (!isLit) {
                      return (
                        <rect
                          // eslint-disable-next-line @eslint-react/no-array-index-key
                          key={floor}
                          x={wx}
                          y={wy}
                          width={WINDOW_SIZE}
                          height={WINDOW_SIZE}
                          rx={1.2}
                          fill={NIGHT.unlit}
                        />
                      );
                    }
                    return (
                      <LitWindow
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={floor}
                        $delaySec={isPreview ? 0 : (index * 3 + floor) * 0.03}
                      >
                        <rect
                          x={wx - 2}
                          y={wy - 2}
                          width={WINDOW_SIZE + 4}
                          height={WINDOW_SIZE + 4}
                          rx={3}
                          fill={NIGHT.lit}
                          opacity={0.25}
                          filter={`url(#${ids.soft})`}
                        />
                        <rect
                          x={wx}
                          y={wy}
                          width={WINDOW_SIZE}
                          height={WINDOW_SIZE}
                          rx={1.2}
                          fill={NIGHT.lit}
                        />
                      </LitWindow>
                    );
                  })}
                </g>
              );
            })}
            <rect
              x={0}
              y={GROUND_Y}
              width={120}
              height={22}
              fill={NIGHT.ground}
            />
          </g>
          <rect
            x={6}
            y={6}
            width={108}
            height={108}
            rx={22}
            fill="none"
            stroke={FELT.cream}
            strokeWidth={5}
            filter={`url(#${ids.fuzz})`}
          />
          <rect
            x={12}
            y={12}
            width={96}
            height={96}
            rx={17}
            fill="none"
            stroke={FELT.cream}
            strokeWidth={1.4}
            strokeDasharray="5 4"
            opacity={0.35}
          />
        </svg>
      </Bounce>
    </ToyShell>
  );
}
